import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ApiEndpoints } from '@app/utils/enums/api-endpoints';
import { environment } from '@env/environment';
import {
  ContentGroup,
  ContentResponse,
  Content,
  UpdateContentBlockPayload,
} from '@app/utils/interfaces/content.interface';
import { OfferSectionData } from '@app/utils/interfaces/offer.interface';
import {
  TeaserSectionResponse,
  TeaserSectionData,
  UpdateTeaserSectionPayload,
} from '@app/utils/interfaces/teaser.interface';
import {
  CategoriesResponse,
  SearchCategoriesResponse,
} from '../../../../utils/interfaces/categories.interface';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly httpClient: HttpClient = inject(HttpClient);

  public getContents(shopId: string): Observable<ContentGroup[]> {
    return this.httpClient
      .get<ContentResponse>(this.baseUrl + ApiEndpoints.GET_CONTENT_FOR_SHOP + shopId)
      .pipe(
        map(({ data: contents }: ContentResponse) => {
          return ContentService.contentGroupsFactory(contents);
        }),
      );
  }

  public getTeaserSection(): Observable<TeaserSectionData> {
    return this.httpClient
      .get<TeaserSectionResponse>(`${this.baseUrl}/api/v1/teaser-section`)
      .pipe(map((response: TeaserSectionResponse) => response.data));
  }

  public getOffersSection(): Observable<OfferSectionData> {
    return this.httpClient.get<unknown>(`${this.baseUrl}/api/v1/offer/promotions`).pipe(
      map((response: unknown) => {
        const typedResponse = response as Partial<OfferSectionData> & {
          data?: Partial<OfferSectionData>;
        };

        const source = typedResponse.data ?? typedResponse;

        return {
          name: source.name ?? '',
          isVisible: source.isVisible ?? true,
          selectionType: source.selectionType ?? 1,
          inventoryItemIds: source.inventoryItemIds ?? [],
        };
      }),
    );
  }

  public updateTeaserSection(payload: UpdateTeaserSectionPayload): Observable<void> {
    const formData = new FormData();

    formData.append('IsVisible', String(payload.isVisible));
    formData.append('Heading', payload.heading);
    formData.append('AdditionalText', payload.additionalText);
    formData.append('Link', payload.link);
    formData.append('HeadingAlignment', String(payload.headingAlignment));
    formData.append('AdditionalTextAlignment', String(payload.additionalTextAlignment));
    formData.append('HeadingColor', payload.headingColor);
    formData.append('AdditionalTextColor', payload.additionalTextColor);

    if (payload.teaserImage) {
      formData.append('TeaserImage', payload.teaserImage, payload.teaserImage.name);
    }

    return this.httpClient.put<void>(`${this.baseUrl}/api/v1/teaser-section`, formData);
  }

  public updateOfferSection(payload: OfferSectionData): Observable<void> {
    return this.httpClient.put<void>(`${this.baseUrl}/api/v1/offer`, payload);
  }

  public updateContentBlock(
    shopId: string,
    apiSectionType: string,
    payload: UpdateContentBlockPayload,
  ): Observable<void> {
    const slug = apiSectionType.startsWith(`${shopId}-`)
      ? apiSectionType
      : `${shopId}-${apiSectionType}`;

    return this.httpClient.put<void>(`${this.baseUrl}/api/v1/content/block/${slug}`, payload);
  }

  private static contentGroupsFactory(contents: Content[]): ContentGroup[] {
    const groups: ContentGroup[] = [
      { groupName: 'home-page', isExpanded: false, contents: [] },
      { groupName: 'footer', isExpanded: false, contents: [] },
    ];

    const footerTypes = [
      'our-shop',
      'our-story',
      'terms-and-conditions',
      'privacy-policy',
      'cookies-policy',
    ];

    contents.forEach((c: Content) => {
      const originalSectionType = c.sectionType;

      c.apiSectionType = c.apiSectionType || originalSectionType;
      c.sectionType = originalSectionType.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

      if (footerTypes.includes(c.sectionType)) {
        groups[1].contents.push(c);
      } else {
        groups[0].contents.push(c);
      }
    });

    return groups;
  }

  public getAllCategories(): Observable<CategoriesResponse> {
    return this.httpClient.get<CategoriesResponse>(`${this.baseUrl}${ApiEndpoints.CATEGORIES}`);
  }

  public searchCategories(query: string): Observable<SearchCategoriesResponse> {
    const params = new HttpParams().set('query', query.trim());

    return this.httpClient.get<SearchCategoriesResponse>(
      `${this.baseUrl}${ApiEndpoints.CATEGORIES_SEARCH}`,
      { params },
    );
  }
}
